import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Calendar, Video, Clock, Plus, Edit2, Trash2,
  X, Loader2, Users, Link as LinkIcon, ExternalLink,
  Check, CheckCircle2, AlertCircle, User, UsersRound, Globe, Lock, UserX
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASS_TYPES = [
  { id: 'individual', label: 'Individual', icon: User, description: '1-on-1 lesson' },
  { id: 'group', label: 'Group', icon: UsersRound, description: 'For a learning group' },
  { id: 'open', label: 'Open', icon: Globe, description: 'Students can enroll' }
];

const ENROLLMENT_TYPES = [
  { id: 'open', label: 'Open Enrollment', description: 'Students can self-enroll' },
  { id: 'closed', label: 'Closed', description: 'Only assigned students' }
];

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const statusColors = {
  scheduled: 'bg-[#4CAF50]/10 text-[#4CAF50]',
  completed: 'bg-[#1A2E6E]/10 text-[#1A2E6E]',
  cancelled: 'bg-[#D72B2B]/10 text-[#D72B2B]',
  full: 'bg-[#F5A623]/10 text-[#F5A623]'
};

const formatDateTimeLocalValue = (dateString) => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

export const AdminClasses = () => {
  const { user, isStrictAdmin } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [filter, setFilter] = useState('all');
  const [conflictInfo, setConflictInfo] = useState(null); // { message, conflicts[] }
  const [noShowTarget, setNoShowTarget] = useState(null); // class object
  const [noShowType, setNoShowType] = useState('student'); // 'student' | 'teacher'
  const [noShowStudentIds, setNoShowStudentIds] = useState([]);
  const [noShowSaving, setNoShowSaving] = useState(false);
  const [completing, setCompleting] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    teacher_id: '',
    class_type: 'individual',
    enrollment_type: 'closed',
    group_id: '',
    student_ids: [],
    max_capacity: 1,
    scheduled_at: '',
    duration_minutes: 60,
    meeting_link: '',
    meeting_type: 'google_meet',
    level: '',
    course_type: ''
  });
  const selectableTeachers = isStrictAdmin ? teachers : teachers.filter(t => t.id === user?.id);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, studentsRes, groupsRes, teachersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/classes?page_size=200`),
        axios.get(`${API_URL}/api/admin/students?page_size=200`),
        axios.get(`${API_URL}/api/admin/groups?page_size=200`),
        axios.get(`${API_URL}/api/admin/teachers?page_size=200`),
      ]);
      setClasses(classesRes.data.data ?? classesRes.data);
      const studentsList = studentsRes.data.data ?? studentsRes.data;
      setStudents(studentsList.filter(s => s.status === 'approved'));
      setGroups(groupsRes.data.data ?? groupsRes.data);
      setTeachers(teachersRes.data.data ?? teachersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (classItem = null) => {
    setConflictInfo(null);
    if (classItem) {
      setEditingClass(classItem);
      setFormData({
        title: classItem.title || '',
        description: classItem.description || '',
        teacher_id: classItem.teacher_id || '',
        class_type: classItem.class_type || 'individual',
        enrollment_type: classItem.enrollment_type || 'closed',
        group_id: classItem.group_id || '',
        student_ids: classItem.student_ids || [],
        max_capacity: classItem.max_capacity || 1,
        scheduled_at: formatDateTimeLocalValue(classItem.scheduled_at),
        duration_minutes: classItem.duration_minutes || 60,
        meeting_link: classItem.meeting_link || '',
        meeting_type: classItem.meeting_type || 'google_meet',
        level: classItem.level || '',
        course_type: classItem.course_type || ''
      });
    } else {
      setEditingClass(null);
      setFormData({
        title: '',
        description: '',
        teacher_id: selectableTeachers[0]?.id || user?.id || '',
        class_type: 'individual',
        enrollment_type: 'closed',
        group_id: '',
        student_ids: [],
        max_capacity: 1,
        scheduled_at: '',
        duration_minutes: 60,
        meeting_link: '',
        meeting_type: 'google_meet',
        level: '',
        course_type: ''
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingClass(null);
    setConflictInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.scheduled_at) {
      toast.error('Please fill in required fields');
      return;
    }

    if (formData.class_type === 'group' && !formData.group_id) {
      toast.error('Please select a group for group classes');
      return;
    }

    setConflictInfo(null);
    setSaving(true);
    try {
      const payload = {
        ...formData,
        teacher_id: formData.teacher_id || undefined,
        scheduled_at: new Date(formData.scheduled_at).toISOString(),
        max_capacity: formData.class_type === 'individual' ? 1 : formData.max_capacity
      };

      if (editingClass) {
        await axios.put(`${API_URL}/api/admin/classes/${editingClass.id}`, payload);
        toast.success('Class updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/classes`, payload);
        toast.success('Class created successfully');
      }

      closeModal();
      fetchData();
    } catch (err) {
      if (err.response?.status === 409) {
        const detail = err.response.data?.detail;
        setConflictInfo(typeof detail === 'object' ? detail : { message: detail, conflicts: [] });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save class');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;

    setDeleting(classId);
    try {
      await axios.delete(`${API_URL}/api/admin/classes/${classId}`);
      toast.success('Class deleted');
      fetchData();
    } catch (err) {
      console.error('Failed to delete class:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete class');
    } finally {
      setDeleting(null);
    }
  };

  const handleComplete = async (classItem) => {
    if (!window.confirm(`Mark "${classItem.title}" as completed? This will process one attendance credit per student.`)) return;
    setCompleting(classItem.id);
    try {
      await axios.put(`${API_URL}/api/admin/classes/${classItem.id}/status`, null, {
        params: { status: 'completed' },
      });
      toast.success('Class completed and attendance credits processed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete class');
    } finally {
      setCompleting(null);
    }
  };

  const openNoShow = (classItem) => {
    setNoShowTarget(classItem);
    setNoShowType('student');
    setNoShowStudentIds(classItem.student_ids?.length === 1 ? [classItem.student_ids[0]] : []);
  };

  const handleNoShow = async () => {
    if (!noShowTarget) return;
    if (noShowType === 'student' && noShowStudentIds.length === 0) {
      toast.error('Select at least one absent student');
      return;
    }
    setNoShowSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/classes/${noShowTarget.id}/no-show`, {
        noshow_type: noShowType,
        student_ids: noShowType === 'student' ? noShowStudentIds : undefined,
      });
      toast.success(
        noShowType === 'teacher'
          ? `Teacher no-show recorded for ${res.data.affected_students} student(s)`
          : `Attendance saved; class is ${res.data.status}`
      );
      setNoShowTarget(null);
      setNoShowStudentIds([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record no-show');
    } finally {
      setNoShowSaving(false);
    }
  };

  const handleClassTypeChange = (type) => {
    let newData = { ...formData, class_type: type, student_ids: [] };
    
    if (type === 'individual') {
      newData.max_capacity = 1;
      newData.enrollment_type = 'closed';
      newData.group_id = '';
    } else if (type === 'group') {
      newData.enrollment_type = 'closed';
    } else if (type === 'open') {
      newData.enrollment_type = 'open';
      newData.group_id = '';
    }
    
    setFormData(newData);
  };

  const toggleStudent = (studentId) => {
    setFormData(prev => ({
      ...prev,
      student_ids: prev.student_ids.includes(studentId)
        ? prev.student_ids.filter(id => id !== studentId)
        : [...prev.student_ids, studentId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStudentNames = (studentIds) => {
    return studentIds
      .map(id => students.find(s => s.id === id)?.name)
      .filter(Boolean)
      .join(', ') || 'No students';
  };

  const getGroupName = (groupId) => {
    return groups.find(g => g.id === groupId)?.name || '';
  };

  const getTeacherName = (teacherId) => {
    return teachers.find(t => t.id === teacherId)?.name || '';
  };

  const filteredClasses = classes.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'scheduled') return ['scheduled', 'full'].includes(c.status);
    if (filter === 'completed') return c.status === 'completed';
    if (filter === 'individual') return c.class_type === 'individual';
    if (filter === 'group') return c.class_type === 'group';
    if (filter === 'open') return c.class_type === 'open';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Classes
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Schedule and manage classes
          </p>
        </div>
        <button
          onClick={() => openModal()}
          data-testid="create-class-btn"
          className="flex items-center gap-2 px-5 py-3 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium"
        >
          <Plus size={20} />
          New Class
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">{classes.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Total</p>
        </div>
        <div className="bg-[#4CAF50]/5 rounded-xl border border-[#4CAF50]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#4CAF50]">
            {classes.filter(c => ['scheduled', 'full'].includes(c.status)).length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Scheduled</p>
        </div>
        <div className="bg-[#1A2E6E]/5 rounded-xl border border-[#1A2E6E]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">
            {classes.filter(c => c.class_type === 'individual').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Individual</p>
        </div>
        <div className="bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#F5A623]">
            {classes.filter(c => c.class_type === 'group').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Group</p>
        </div>
        <div className="bg-[#D72B2B]/5 rounded-xl border border-[#D72B2B]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#D72B2B]">
            {classes.filter(c => c.class_type === 'open' || c.enrollment_type === 'open').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Open</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white rounded-xl p-1.5 border border-[#E8EAF0] w-fit flex-wrap">
        {[
          { id: 'all', label: 'All' },
          { id: 'scheduled', label: 'Scheduled' },
          { id: 'individual', label: 'Individual' },
          { id: 'group', label: 'Group' },
          { id: 'open', label: 'Open' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === tab.id
                ? 'bg-[#1A2E6E] text-white'
                : 'text-[#1A2E6E]/70 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Classes List */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
        {filteredClasses.length > 0 ? (
          <div className="divide-y divide-[#E8EAF0]">
            {filteredClasses.map((classItem, index) => (
              <motion.div
                key={classItem.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className="p-4 lg:p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Icon */}
                  <div className={`
                    w-14 h-14 rounded-xl flex items-center justify-center shrink-0
                    ${classItem.class_type === 'individual' ? 'bg-[#1A2E6E]/10' : 
                      classItem.class_type === 'group' ? 'bg-[#F5A623]/10' : 'bg-[#4CAF50]/10'}
                  `}>
                    {classItem.class_type === 'individual' ? (
                      <User className="w-7 h-7 text-[#1A2E6E]" />
                    ) : classItem.class_type === 'group' ? (
                      <UsersRound className="w-7 h-7 text-[#F5A623]" />
                    ) : (
                      <Globe className="w-7 h-7 text-[#4CAF50]" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-[#1A2E6E] text-lg truncate">
                        {classItem.title}
                      </h3>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[classItem.status]}`}>
                          {classItem.status}
                        </span>
                        {classItem.enrollment_type === 'open' && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#4CAF50]/10 text-[#4CAF50]">
                            <Globe size={10} />
                            Open
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#1A2E6E]/70 mb-2">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={14} />
                        {formatDate(classItem.scheduled_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock size={14} />
                        {classItem.duration_minutes}min
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users size={14} />
                        {classItem.student_ids?.length || 0} / {classItem.max_capacity || 1}
                      </span>
                      {getTeacherName(classItem.teacher_id) && (
                        <span className="flex items-center gap-1.5">
                          <User size={14} />
                          {getTeacherName(classItem.teacher_id)}
                        </span>
                      )}
                      {classItem.level && (
                        <span className="px-2 py-0.5 bg-[#1A2E6E]/5 rounded text-xs font-medium">
                          {classItem.level}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-[#1A2E6E]/60 truncate">
                      {classItem.group_id 
                        ? `Group: ${getGroupName(classItem.group_id)}`
                        : getStudentNames(classItem.student_ids || [])
                      }
                    </p>

                    {classItem.meeting_link && (
                      <a
                        href={classItem.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-sm text-[#4CAF50] hover:underline"
                      >
                        <LinkIcon size={14} />
                        {classItem.meeting_type === 'google_meet' ? 'Google Meet' : 'Zoom'}
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>

                  {/* Capacity indicator */}
                  <div className="w-24 shrink-0">
                    <div className="text-center mb-1">
                      <span className="text-lg font-bold text-[#1A2E6E]">
                        {classItem.student_ids?.length || 0}
                      </span>
                      <span className="text-[#1A2E6E]/50">/{classItem.max_capacity || 1}</span>
                    </div>
                    <div className="h-2 bg-[#E8EAF0] rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          (classItem.student_ids?.length || 0) >= (classItem.max_capacity || 1)
                            ? 'bg-[#F5A623]' : 'bg-[#4CAF50]'
                        }`}
                        style={{ 
                          width: `${Math.min(100, ((classItem.student_ids?.length || 0) / (classItem.max_capacity || 1)) * 100)}%` 
                        }}
                      />
                    </div>
                    <p className="text-xs text-center text-[#1A2E6E]/50 mt-1">
                      {(classItem.max_capacity || 1) - (classItem.student_ids?.length || 0)} spots
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {['scheduled', 'full'].includes(classItem.status) && (
                      <button
                        onClick={() => handleComplete(classItem)}
                        disabled={completing === classItem.id}
                        className="p-2.5 text-[#4CAF50] hover:bg-[#4CAF50]/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Mark completed"
                        data-testid={`complete-class-${classItem.id}`}
                      >
                        {completing === classItem.id ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                      </button>
                    )}
                    {['scheduled', 'full'].includes(classItem.status) && (
                      <button
                        onClick={() => openNoShow(classItem)}
                        className="p-2.5 text-[#F5A623] hover:bg-[#F5A623]/10 rounded-lg transition-colors"
                        title="Record no-show"
                      >
                        <UserX size={18} />
                      </button>
                    )}
                    {!['completed', 'cancelled'].includes(classItem.status) && (
                      <>
                        <button
                          onClick={() => openModal(classItem)}
                          className="p-2.5 text-[#1A2E6E] hover:bg-[#1A2E6E]/10 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(classItem.id)}
                          disabled={deleting === classItem.id}
                          className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          {deleting === classItem.id ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Calendar className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1A2E6E] mb-2">No Classes</h3>
            <p className="text-[#1A2E6E]/60 mb-4">
              {filter === 'all' ? 'Create your first class' : 'No classes match this filter'}
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-xl font-bold text-[#1A2E6E]">
                  {editingClass ? 'Edit Class' : 'New Class'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Conflict banner */}
              {conflictInfo && (
                <div className="mx-6 mt-4 p-4 rounded-xl bg-[#D72B2B]/5 border border-[#D72B2B]/20">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertCircle size={16} className="text-[#D72B2B] shrink-0 mt-0.5" />
                    <p className="text-sm font-semibold text-[#D72B2B]">{conflictInfo.message}</p>
                  </div>
                  {conflictInfo.conflicts?.map((c, i) => (
                    <p key={i} className="text-xs text-[#D72B2B]/80 ml-6">
                      • {c.title} — {new Date(c.scheduled_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })} ({c.duration_minutes}min)
                    </p>
                  ))}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Class Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-3">
                    Class Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {CLASS_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => handleClassTypeChange(type.id)}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            formData.class_type === type.id
                              ? 'border-[#D72B2B] bg-[#D72B2B]/5'
                              : 'border-[#E8EAF0] hover:border-[#1A2E6E]/30'
                          }`}
                        >
                          <Icon className={`w-6 h-6 mx-auto mb-2 ${
                            formData.class_type === type.id ? 'text-[#D72B2B]' : 'text-[#1A2E6E]/60'
                          }`} />
                          <p className={`text-sm font-medium ${
                            formData.class_type === type.id ? 'text-[#D72B2B]' : 'text-[#1A2E6E]'
                          }`}>{type.label}</p>
                          <p className="text-xs text-[#1A2E6E]/50 mt-1">{type.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Teacher */}
                {isStrictAdmin && selectableTeachers.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Teacher</label>
                    <select
                      value={formData.teacher_id}
                      onChange={(e) => { setFormData({ ...formData, teacher_id: e.target.value }); setConflictInfo(null); }}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                    >
                      {selectableTeachers.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., English Conversation Class"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Class details..."
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none"
                  />
                </div>

                {/* Group Selection (for group classes) */}
                {formData.class_type === 'group' && (
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                      Learning Group <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.group_id}
                      onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                      required={formData.class_type === 'group'}
                    >
                      <option value="">Select a group</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.student_count || 0} students)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Student Selection (for individual classes) */}
                {formData.class_type === 'individual' && (
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                      Student <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.student_ids[0] || ''}
                      onChange={(e) => setFormData({ ...formData, student_ids: e.target.value ? [e.target.value] : [] })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                      required={formData.class_type === 'individual'}
                    >
                      <option value="">Select a student</option>
                      {students.map(student => (
                        <option key={student.id} value={student.id}>
                          {student.name} ({student.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Capacity (for open classes) */}
                {formData.class_type === 'open' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Max Capacity</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={formData.max_capacity}
                        onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) || 1 })}
                        className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Level</label>
                      <select
                        value={formData.level}
                        onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                      >
                        <option value="">Any level</option>
                        {LEVELS.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                      Date & Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_at}
                      onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Duration</label>
                    <select
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                    >
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>60 minutes</option>
                      <option value={90}>90 minutes</option>
                      <option value={120}>120 minutes</option>
                    </select>
                  </div>
                </div>

                {/* Meeting Link */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Meeting Link</label>
                    <input
                      type="url"
                      value={formData.meeting_link}
                      onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                      placeholder="https://meet.google.com/..."
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Type</label>
                    <select
                      value={formData.meeting_type}
                      onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                    >
                      <option value="google_meet">Google Meet</option>
                      <option value="zoom">Zoom</option>
                    </select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-3 px-4 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-3 px-4 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Check size={18} />
                    )}
                    {editingClass ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No-show Modal */}
      <AnimatePresence>
        {noShowTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setNoShowTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EAF0]">
                <div className="flex items-center gap-2">
                  <UserX size={20} className="text-[#F5A623]" />
                  <h3 className="text-lg font-semibold text-[#1A2E6E]">Record No-Show</h3>
                </div>
                <button onClick={() => setNoShowTarget(null)} className="text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-5">
                <p className="text-sm text-[#1A2E6E]/70">
                  Recording a no-show for <span className="font-semibold text-[#1A2E6E]">{noShowTarget.title}</span>{' '}
                  will apply your credit policy. A group class with other students present will be completed, not cancelled.
                </p>
                <div>
                  <p className="text-sm font-medium text-[#1A2E6E] mb-3">Who didn't show up?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setNoShowType('student')}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        noShowType === 'student'
                          ? 'border-[#F5A623] bg-[#F5A623]/5'
                          : 'border-[#E8EAF0] hover:border-[#1A2E6E]/20'
                      }`}
                    >
                      <User className={`w-6 h-6 mx-auto mb-1.5 ${noShowType === 'student' ? 'text-[#F5A623]' : 'text-[#1A2E6E]/50'}`} />
                      <p className={`text-sm font-medium ${noShowType === 'student' ? 'text-[#F5A623]' : 'text-[#1A2E6E]'}`}>Student</p>
                      <p className="text-xs text-[#1A2E6E]/50 mt-0.5">May deduct credit</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNoShowType('teacher')}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        noShowType === 'teacher'
                          ? 'border-[#F5A623] bg-[#F5A623]/5'
                          : 'border-[#E8EAF0] hover:border-[#1A2E6E]/20'
                      }`}
                    >
                      <User className={`w-6 h-6 mx-auto mb-1.5 ${noShowType === 'teacher' ? 'text-[#F5A623]' : 'text-[#1A2E6E]/50'}`} />
                      <p className={`text-sm font-medium ${noShowType === 'teacher' ? 'text-[#F5A623]' : 'text-[#1A2E6E]'}`}>Teacher</p>
                      <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Refunds credits</p>
                    </button>
                  </div>
                </div>
                {noShowType === 'student' && (
                  <div>
                    <p className="text-sm font-medium text-[#1A2E6E] mb-2">Absent students</p>
                    <div className="max-h-48 overflow-y-auto rounded-xl border border-[#E8EAF0] divide-y divide-[#E8EAF0]">
                      {(noShowTarget.student_ids || []).map(studentId => {
                        const student = students.find(item => item.id === studentId);
                        return (
                          <label key={studentId} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#1A2E6E]/5">
                            <input
                              type="checkbox"
                              checked={noShowStudentIds.includes(studentId)}
                              onChange={() => setNoShowStudentIds(current => (
                                current.includes(studentId)
                                  ? current.filter(id => id !== studentId)
                                  : [...current, studentId]
                              ))}
                              className="w-4 h-4 accent-[#F5A623]"
                            />
                            <span className="text-sm text-[#1A2E6E]">{student?.name || 'Student'}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setNoShowTarget(null)}
                    className="flex-1 py-2.5 px-4 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNoShow}
                    disabled={noShowSaving}
                    className="flex-1 py-2.5 px-4 bg-[#F5A623] text-white rounded-xl hover:bg-[#C97D0A] transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {noShowSaving ? <Loader2 size={16} className="animate-spin" /> : <UserX size={16} />}
                    Confirm No-Show
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminClasses;
